import { Dtos } from "@inrev/common";
import { useFetchAdminActionItems } from "@portal/api/action-item/admin";
import { useFetchTrailing90DayGwp } from "@portal/api/dashboard/admin";
import {
	useFetchAccountPreviews,
	useFetchBondPreviews,
	useFetchBondRequestPreviews,
} from "@portal/api/previews";
import {
	formatMetricCardCurrency,
	TrendMetricCard,
	type TrendMetricCardProps,
} from "@portal/components/patterns/metric-card";
import { Spinner } from "@portal/components/primitives";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useMemo } from "react";
import type { DashboardScope } from "../scope";
import { computeMetrics } from "./computeMetrics";

const EMPTY_ACCOUNT_PREVIEWS: Dtos.SuretyAccount.Preview.Get.Response[] = [];
const EMPTY_BOND_PREVIEWS: Dtos.SuretyBond.Preview.Get.Response[] = [];
const EMPTY_BOND_REQUEST_PREVIEWS: Dtos.SuretyQuote.Preview.Get.Response[] = [];
const EMPTY_ACTION_ITEMS: Dtos.ActionItem.Get.Response[] = [];
const EMPTY_SERIES: number[] = [];

type DashboardMetricsViewProps = {
	scope: DashboardScope;
};

export const DashboardMetricsView = ({ scope }: DashboardMetricsViewProps) => {
	const { user } = useAuthenticatedUser();

	const { accountPreviews } = useFetchAccountPreviews();
	const { bondPreviews } = useFetchBondPreviews();
	const { bondRequestPreviews } = useFetchBondRequestPreviews();
	const { actionItems } = useFetchAdminActionItems();
	const { trailing90DayGwp, trailing90DayGwpIsLoading } = useFetchTrailing90DayGwp(
		scope === "me" ? user.id : undefined,
	);

	const actionItemsList = useMemo(
		() => (actionItems ? Object.values(actionItems) : EMPTY_ACTION_ITEMS),
		[actionItems],
	);

	const metrics = useMemo(
		() =>
			computeMetrics({
				scope,
				currentUserId: user.id,
				accountPreviews: accountPreviews ?? EMPTY_ACCOUNT_PREVIEWS,
				bondPreviews: bondPreviews ?? EMPTY_BOND_PREVIEWS,
				bondRequestPreviews: bondRequestPreviews ?? EMPTY_BOND_REQUEST_PREVIEWS,
				actionItems: actionItemsList,
			}),
		[scope, user.id, accountPreviews, bondPreviews, bondRequestPreviews, actionItemsList],
	);

	const { trends } = metrics;

	const trailing90DayGwpCurrency = formatMetricCardCurrency(trailing90DayGwp?.gwp ?? 0);
	const openExposureCurrency = formatMetricCardCurrency(metrics.openExposure);

	const cards: TrendMetricCardProps[] = [
		{
			testId: "metric-open-submissions",
			label: "Open Submissions",
			value: metrics.openSubmissions,
			deltaFormat: "count",
			tone: "accent",
			caption: "last 30d",
			...trends.openSubmissions,
		},
		{
			testId: "metric-open-bond-requests",
			label: "Open Bond Requests",
			value: metrics.openBondRequests,
			deltaFormat: "count",
			tone: "accent",
			caption: "last 30d",
			...trends.openBondRequests,
		},
		{
			testId: "metric-action-item-accounts",
			label: "Action Item Accounts",
			value: metrics.actionItemAccounts,
			deltaFormat: "count",
			tone: "accent",
			caption: "last 30d",
			...trends.actionItemAccounts,
		},
		{
			testId: "metric-trailing-90-day-gwp",
			label: "Trailing 90d GWP",
			value: trailing90DayGwpIsLoading ? (
				<Spinner data-testid="metric-gwp-spinner" />
			) : (
				trailing90DayGwpCurrency.display
			),
			valueTooltip: trailing90DayGwpIsLoading ? undefined : trailing90DayGwpCurrency.full,
			series: trailing90DayGwp?.series ?? EMPTY_SERIES,
			current: trailing90DayGwp?.gwp,
			prior: trailing90DayGwp?.priorGwp,
			deltaFormat: "percent",
			tone: "confirm",
		},
		{
			testId: "metric-open-exposure",
			label: "Open Exposure",
			value: openExposureCurrency.display,
			valueTooltip: openExposureCurrency.full,
			deltaFormat: "percent",
			tone: "confirm",
			caption: "last 30d",
			...trends.openExposure,
		},
	];

	return (
		<div className="grid max-w-8xl grid-cols-3 gap-4 lg:grid-cols-5">
			{cards.map((card) => (
				<TrendMetricCard key={card.testId} {...card} />
			))}
		</div>
	);
};
